import { FC, useState } from 'react';

import { BodySmall, Box, Button, QRIcon, Skeleton, Tooltip } from '@parspec/pixel';

import AuthenticatorModal from './AuthenticatiorModal';
import { useGetRecoveryCodeCountQuery, useGetTwoFactorPreferencesQuery, useTwoFactorChangePreferenceMutation } from 'src/features/Authentication/queries';
import { AUTHENTICATOR_METHOD_DISABLED, SSO_DISABLED_MSG } from 'src/features/shared/constants';
import { useGetUserProfileInfoQuery } from '../../queries';
import RecoveryCodeModal from './RecoveryCodeModal';
import InfoCard from '../shared/components/InfoCard';

interface MFAProps {
    handleSnackbarMsg: (msg: string) => void;
}

export const MFA: FC<MFAProps> = ({ handleSnackbarMsg }) => {
    const [showMfaModal, setShowMfaModal] = useState(false);
    const [showRecoveryCodeModal, setShowRecoveryCodeModal] = useState(false);

    const { data: userData } = useGetUserProfileInfoQuery();

    const { data: mfaInfo, isLoading: mfaInfoLoading } = useGetTwoFactorPreferencesQuery();

    const isSSOEnabled = Boolean(userData?.data?.is_sso_enabled);
    const isMFAEnabled = Boolean(mfaInfo?.data?.is_authenticator_app_enabled);

    const { data: recoveryCodeCountData, isFetching: recoveryCodeCountLoading } = useGetRecoveryCodeCountQuery({ enabled: isMFAEnabled });

    const { mutateAsync: updatePreferences, isLoading: updatePreferenceLoading } = useTwoFactorChangePreferenceMutation();

    const handleToggleMfaModal = () => setShowMfaModal((old) => !old);

    const handleToggleRecoveryCodeModal = () => setShowRecoveryCodeModal((old) => !old);

    const handleClickDisableMfa = async () => {
        await updatePreferences({ ...mfaInfo!.data, is_authenticator_app_enabled: false, is_setup_dialog_box_enabled: false });
        handleSnackbarMsg(AUTHENTICATOR_METHOD_DISABLED);
    };

    const onClickGenerateCode = async () => {
        handleToggleRecoveryCodeModal();
    };

    const onClickEnableDisableButton = () => {
        if (isMFAEnabled) {
            return handleClickDisableMfa();
        }
        return handleToggleMfaModal();
    };

    return (
        <>
            <Box>
                {mfaInfoLoading ? <Skeleton height="75px" variant="rectangular" /> :
                    <InfoCard
                        title="Multi-Factor Authentication (MFA)"
                        subTitle="Authenticator App"
                        helperText="Use a mobile app to generate a verification code."
                        isOptionEnabled={isMFAEnabled}
                        onClickEnableDisableButton={onClickEnableDisableButton}
                        isLoading={updatePreferenceLoading}
                        isCardDisabled={isSSOEnabled}
                        icon={<QRIcon fontSize="xl" bgColor={isMFAEnabled ? 'primary' : 'neutral'} color={'inherit'} />}
                        tooltipMsg={isSSOEnabled ? SSO_DISABLED_MSG : ''}
                    />}
                {isMFAEnabled && !isSSOEnabled ?
                    recoveryCodeCountLoading ? <Box mt={4}><Skeleton variant='rectangular' height='75px' width='50%' /></Box> :
                        <Box mt={4}>
                            <Box>

                                <BodySmall limit={false} sx={{ textWrap: 'nowrap' }}>
                                    {recoveryCodeCountData?.data?.count || 0} of 16 recovery codes used.
                                </BodySmall>
                                <Box mt={3}>
                                    <Tooltip title={isSSOEnabled ? SSO_DISABLED_MSG : ''}>
                                        <span>
                                            <Button color="secondary" variant="outlined" onClick={onClickGenerateCode} disabled={isSSOEnabled}>
                                                Generate New Codes
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Box>
                            </Box>
                            <Box bgcolor="primary.light" border="1px solid" borderColor="primary.main" py={1} px={2} mr={20} borderRadius={1} mt={2}>
                                <Box display={'flex'} alignItems={'flex-end'}>
                                    <BodySmall limit={false} color="primary.main">
                                        <span style={{ fontWeight: 700 }}>Recovery codes are single-use, last resort passcodes.</span> They can be used to access your account in case of a lost
                                        second factor. <span style={{ fontWeight: 700 }}>After generating new codes, your old codes will stop functioning.</span>
                                    </BodySmall>
                                </Box>
                            </Box>
                        </Box> : null}
            </Box>
            {showMfaModal && <AuthenticatorModal open={showMfaModal} onClose={handleToggleMfaModal} handleSnackBarMsg={handleSnackbarMsg} onClickGenerateCode={onClickGenerateCode} />}
            {showRecoveryCodeModal && <RecoveryCodeModal open={showRecoveryCodeModal} onClose={handleToggleRecoveryCodeModal} handleSnackbarMsg={handleSnackbarMsg} />}
        </>
    );
};
